/**
 * Categorical palettes — spec §G8: "Categorical order fixed (country palette) — never
 * repaint survivors on filter".
 *
 * The country palette is the mockup's `CPAL`, and the rule attached to it is the reason it
 * lives in one module rather than at each call site: Burkina Faso is green in the dock
 * popover's "By country" bars, in the §F4 splash bars and in the §F7 dashboard's
 * cross-country lines, whichever countries happen to be in scope. A palette indexed by
 * position — `colors[i]` down a filtered array — would recolour Mali the moment Burkina
 * Faso dropped out of the filter, which is exactly what §G8 forbids.
 *
 * These four are also *not* the domain colours and *not* the §G status colours (§G8: "status
 * colours … never reused as series colours"), so a green bar here never reads as "open".
 */
import type { Country } from './state';

/** ISO3 → the country's fixed series colour (mockup `CPAL`). */
export const COUNTRY_COLORS: Record<Exclude<Country, 'ALL'>, string> = {
  BFA: '#2E7D32',
  MLI: '#2B6CB0',
  NER: '#C77B21',
  NGA: '#8E3B62',
};

/**
 * §G8's "fixed order" — the sequence every country chart lays its categories out in,
 * independent of whatever order an API happened to answer in.
 */
export const COUNTRY_ORDER: Exclude<Country, 'ALL'>[] = ['BFA', 'MLI', 'NER', 'NGA'];

/** Fallback ink for an unknown code, so a chart never renders a `undefined` fill. */
export const UNKNOWN_SERIES_COLOR = '#8B958A';

/** The colour for one ISO3 code; unknown codes get the neutral ink rather than nothing. */
export function countryColor(iso3: string): string {
  return COUNTRY_COLORS[iso3 as Exclude<Country, 'ALL'>] ?? UNKNOWN_SERIES_COLOR;
}
